/**
 * The reader's colour scheme (US-23.6).
 *
 * <p>Three states, not two: "light", "dark", and "follow the operating system" — which is the default,
 * because most people have already answered this question once, for everything they use.
 *
 * <p>An explicit choice is written as `data-theme` on `<html>`; "system" removes the attribute and
 * lets the `prefers-color-scheme` rules in dark.css decide. The choice is remembered per browser in
 * `localStorage`: it is a preference of the person at this screen, not of the roastery, so it has no
 * business on the server.
 *
 * <p>The same key and the same rule are duplicated, in four lines of inline script, in each
 * application's index.html — the attribute has to be on `<html>` before the first paint, and by the
 * time this module is parsed the page has already been painted white once.
 */
export type ThemePreference = "light" | "dark" | "system";

export const THEME_STORAGE_KEY = "roastery.theme";

export function readThemePreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return stored === "light" || stored === "dark" ? stored : "system";
  } catch {
    // A browser with site data switched off is not broken; it simply follows the system every time.
    return "system";
  }
}

export function applyThemePreference(preference: ThemePreference): void {
  const root = document.documentElement;
  if (preference === "system") {
    delete root.dataset.theme;
  } else {
    root.dataset.theme = preference;
  }
  try {
    if (preference === "system") {
      window.localStorage.removeItem(THEME_STORAGE_KEY);
    } else {
      window.localStorage.setItem(THEME_STORAGE_KEY, preference);
    }
  } catch {
    // Nothing to do: the screen already changed, it just will not be remembered.
  }
}
